import { useRemoveCharger, type Charger } from '../../api/chargers';
import { ApiError } from '../../api/client';
import { useDialog } from '../../components/dialogs/DialogProvider';
import { useToast } from '../../components/Toast';
import { chargerName } from './status';

/**
 * "Remove charger", with its confirmation. Resolves true once removed. A charger with sessions
 * is retired (hidden, can't charge, history kept, can be restored); one never used is erased.
 */
export function useRemoveChargerFlow() {
  const dialog = useDialog();
  const toast = useToast();
  const remove = useRemoveCharger();
  return async (c: Charger): Promise<boolean> => {
    const name = chargerName(c);
    if (c.activeSessions.length) {
      toast(`A session is in progress on ${name}. Stop it first, then remove the charger.`);
      return false;
    }
    const ok = await dialog.confirm({
      title: `Remove ${name}?`,
      message: `It disappears from Pulsepoint and can't charge, even if it connects. Its past sessions stay in reports. You can restore it from "Show removed chargers". A charger that has never been used is deleted completely.`,
      confirmLabel: 'Remove',
      danger: true,
    });
    if (!ok) return false;
    try {
      const { erased } = await remove.mutateAsync(c.id);
      toast(erased ? `${name} was deleted. It had never been used.` : `${name} was removed.`);
      return true;
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Couldn't remove the charger. Please try again.");
      return false;
    }
  };
}
